import { useUser } from "@clerk/nextjs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTRPC } from "@/frontend/lib/trpc";
import { useDebounce } from "./utils";

// QUERIES
export function useGoogleDriveTokenQuery() {
  const { user } = useUser();
  const trpc = useTRPC();
  return useQuery({
    ...trpc.googleDrive.getGoogleDriveTokenStatus.queryOptions(),
    enabled: !!user,
  });
}

export function useGoogleDriveSearchQuery(query: string, pageToken?: string) {
  const { user } = useUser();
  const trpc = useTRPC();
  const debounced = useDebounce(query, 300);
  
  return useQuery({
    ...trpc.googleDrive.searchGoogleDriveFiles.queryOptions({ 
      query: debounced, 
      pageToken 
    }),
    enabled: !!user,
  });
}

export function useGoogleDriveFileQuery(fileId: string | null) {
  const { user } = useUser();
  const trpc = useTRPC();
  return useQuery({
    ...trpc.googleDrive.getGoogleDriveFile.queryOptions({ fileId: fileId! }),
    enabled: Boolean(fileId) && !!user,
  });
}

// MUTATIONS
export function useUploadFileToGoogleDrive() {
  const trpc = useTRPC();
  return useMutation(trpc.googleDrive.uploadFileToGoogleDrive.mutationOptions());
}

export function useDisconnectGoogleDrive() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  return useMutation({
    ...trpc.googleDrive.disconnectGoogleDrive.mutationOptions(),
    onSettled: async () => {
      await queryClient.invalidateQueries(trpc.googleDrive.getGoogleDriveTokenStatus.queryFilter());
    },
  });
}

export function usePreUploadGoogleDriveFile() {
  const trpc = useTRPC();
  return useMutation(trpc.googleDrive.preUploadGoogleDriveFile.mutationOptions());
}

export function useExportJamFileToGoogleDrive() {
  const trpc = useTRPC();
  return useMutation(trpc.googleDrive.exportJamFileToGoogleDrive.mutationOptions());
}